import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../../config/api';

export interface TwilioJobRun {
  id: number;
  user_id: number;
  configuration_id: number;
  job_type: 'manual' | 'automated';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  messages_synced: number;
  calls_synced: number;
  total_messages: number;
  total_calls: number;
  error_message: string | null;
  error_details: string | null;
  sync_interval_hours: string | null;
  force_full_sync: boolean;
}

export interface TwilioJobRunListResponse {
  job_runs: TwilioJobRun[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TwilioJobRunStats {
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  in_progress_runs: number;
  last_run_at: string | null;
  average_duration_seconds: number | null;
  total_messages_synced: number;
  total_calls_synced: number;
}

export interface JobRunFilters {
  configuration_id?: number;
  status?: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  job_type?: 'manual' | 'automated';
  page?: number;
  page_size?: number;
}

export const jobRunsApi = createApi({
  reducerPath: 'jobRunsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/api/v1/twilio/job-runs'),
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['JobRun'],
  endpoints: (builder) => ({
    getJobRuns: builder.query<TwilioJobRunListResponse, JobRunFilters>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.configuration_id) params.append('configuration_id', filters.configuration_id.toString());
        if (filters.status) params.append('status', filters.status);
        if (filters.job_type) params.append('job_type', filters.job_type);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.page_size) params.append('page_size', filters.page_size.toString());
        
        return `/?${params.toString()}`;
      },
      providesTags: ['JobRun'],
    }),
    getJobRun: builder.query<TwilioJobRun, number>({
      query: (jobRunId) => `/${jobRunId}`,
      providesTags: ['JobRun'],
    }),
    getJobRunStats: builder.query<TwilioJobRunStats, { configuration_id?: number }>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.configuration_id) params.append('configuration_id', filters.configuration_id.toString());
        return `/stats?${params.toString()}`;
      },
      providesTags: ['JobRun'],
    }),
    createJobRun: builder.mutation<TwilioJobRun, { configuration_id: number; job_type: 'manual' | 'automated'; sync_interval_hours?: string; force_full_sync?: boolean }>({
      query: (jobData) => ({
        url: '/',
        method: 'POST',
        body: jobData,
      }),
      invalidatesTags: ['JobRun'],
    }),
    startJobRun: builder.mutation<TwilioJobRun, number>({
      query: (jobRunId) => ({
        url: `/${jobRunId}/start`,
        method: 'POST',
      }),
      invalidatesTags: ['JobRun'],
    }),
    completeJobRun: builder.mutation<TwilioJobRun, { jobRunId: number; messages_synced?: number; calls_synced?: number; total_messages?: number; total_calls?: number }>({
      query: ({ jobRunId, ...data }) => ({
        url: `/${jobRunId}/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['JobRun'],
    }),
    failJobRun: builder.mutation<TwilioJobRun, { jobRunId: number; error_message: string; error_details?: string }>({
      query: ({ jobRunId, ...data }) => ({
        url: `/${jobRunId}/fail`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['JobRun'],
    }),
    cancelJobRun: builder.mutation<{ message: string }, { jobRunId: number }>({
      query: ({ jobRunId }) => ({
        url: `/${jobRunId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['JobRun'],
    }),
    cleanupOldJobRuns: builder.mutation<{ message: string; cleaned_count: number }, { days_to_keep?: number }>({
      query: (params = {}) => ({
        url: '/cleanup',
        method: 'DELETE',
        body: params,
      }),
      invalidatesTags: ['JobRun'],
    }),
  }),
});

export const {
  useGetJobRunsQuery,
  useGetJobRunQuery,
  useGetJobRunStatsQuery,
  useCreateJobRunMutation,
  useStartJobRunMutation,
  useCompleteJobRunMutation,
  useFailJobRunMutation,
  useCancelJobRunMutation,
  useCleanupOldJobRunsMutation,
} = jobRunsApi;
